// The replay viewer's own DOM over the match stage: the controls bar (play/pause, speed, timeline, vision,
// link, exit), the hero panel (portrait, HP, K/D/A, respawn; a click follows that hero) and the end banner
// shown when a recording ends without a results screen. Rendering and input only — replayViewer.ts owns the
// playback and decides what every button does.
import type { Team } from '../../core/layout.js';
import type { ReplayRosterEntry } from '../../shared/replay/format.js';
import { createDomWriter } from '../hud.js';
import { escapeHtml, playerLabel } from '../nameStore.js';
import { getLang, t } from '../i18n/index.js';
import { heroName, teamName } from '../i18n/names.js';
import { SPEEDS, mmss, type Speed } from './playback.js';
import type { Vision } from './replayView.js';

export const SEEK_STEP_SEC = 5;

const visions = (): [Vision, string][] => [[null, t('replay.visionAll')], ['a', teamName('a')], ['b', teamName('b')]];
const visionKey = (v: Vision) => v ?? 'all';
const speedLabel = (s: Speed) => `×${getLang() === 'ru' ? String(s).replace('.', ',') : String(s)}`;

export type ReplayUiActions = {
  toggle(): void;
  seekSec(sec: number): void;
  seekBy(sec: number): void;
  speed(s: Speed): void;
  vision(v: Vision): void;
  /** A hero card was clicked (the viewer toggles following it). */
  hero(seat: number): void;
  copyLink(): void;
  restart(): void;
  exit(): void;
};

export type HeroCardState = { hp: number; maxHp: number; alive: boolean; visible: boolean; respawnIn: number | null; kda: readonly [number, number, number] };

export type ReplayUiState = {
  playing: boolean; speed: Speed; timeSec: number; totalSec: number; vision: Vision; follow: number | null;
  heroes: readonly HeroCardState[];
  /** The end banner, when the recording is over and there is no results screen to show instead. */
  end: { title: string; sub: string } | null;
};

export function createReplayUi(host: HTMLElement, roster: readonly ReplayRosterEntry[], viewerSeat: number | null, act: ReplayUiActions) {
  const root = document.createElement('div');
  root.className = 'replay-ui';
  const seatsOf = (team: Team) => roster.flatMap((r, i) => (r.team === team ? [i] : []));
  const card = (i: number) => {
    const r = roster[i]!;
    return `
      <button type="button" class="rp-hero ${r.team}${i === viewerSeat ? ' me' : ''}" data-seat="${i}" aria-pressed="false" title="${t('replay.follow')}">
        <img class="rp-face" src="portraits/${r.kind}.jpg" alt="" width="480" height="640" loading="lazy">
        <span class="rp-who"><b>${heroName(r.kind)}</b><small>${r.name === null ? t('replay.bot') : escapeHtml(playerLabel(r.name))}</small></span>
        <span class="rp-bar"><i></i></span>
        <span class="rp-kda"></span>
        <span class="rp-resp"></span>
      </button>`;
  };
  root.innerHTML = `
    <div class="rp-heroes" aria-label="${t('codex.heroes')}">
      ${(['a', 'b'] as const).map(team => `<div class="rp-side ${team}"><span class="rp-side-name">${teamName(team)}</span>${seatsOf(team).map(card).join('')}</div>`).join('')}
    </div>
    <p class="rp-note" id="rpNote" role="status" hidden></p>
    <div class="rp-end" id="rpEnd" hidden>
      <span id="rpEndTitle"></span><small id="rpEndSub"></small>
      <div class="rp-end-actions">
        <button type="button" class="front-btn" id="rpAgain">${t('replay.again')}</button>
        <button type="button" class="front-btn front-btn-primary" id="rpEndExit">${t('replay.exit')}</button>
      </div>
    </div>
    <div class="rp-bar-main" role="toolbar" aria-label="${t('replay.controls')}">
      <input type="range" class="rp-seek" id="rpSeek" min="0" max="1" step="0.1" value="0" aria-label="${t('replay.time')}">
      <div class="rp-row">
        <button type="button" class="rp-btn rp-play" id="rpPlay" title="${t('replay.playTitle')}">${t('replay.pause')}</button>
        <span class="rp-time" id="rpTime">0:00 / 0:00</span>
        <div class="rp-seg" role="group" aria-label="${t('replay.speed')}">${SPEEDS.map(s => `<button type="button" class="rp-btn" data-speed="${s}" aria-pressed="false">${speedLabel(s)}</button>`).join('')}</div>
        <div class="rp-seg" role="group" aria-label="${t('replay.vision')}">${visions().map(([v, l]) => `<button type="button" class="rp-btn" data-vision="${visionKey(v)}" aria-pressed="false">${l}</button>`).join('')}</div>
        <span class="rp-gap"></span>
        <button type="button" class="rp-btn" id="rpLink">${t('replay.copyLink')}</button>
        <button type="button" class="rp-btn rp-exit" id="rpExit">${t('replay.exit')}</button>
      </div>
    </div>`;
  host.appendChild(root);
  const $ = <T extends HTMLElement = HTMLElement>(id: string) => root.querySelector('#' + id) as T;
  const seek = $<HTMLInputElement>('rpSeek'), play = $('rpPlay'), time = $('rpTime'), note = $('rpNote');
  const end = $('rpEnd'), endTitle = $('rpEndTitle'), endSub = $('rpEndSub');
  const speedBtns = [...root.querySelectorAll<HTMLButtonElement>('[data-speed]')];
  const visionBtns = [...root.querySelectorAll<HTMLButtonElement>('[data-vision]')];
  const cards = [...root.querySelectorAll<HTMLButtonElement>('.rp-hero')].map(el => ({
    el, seat: Number(el.dataset.seat),
    bar: el.querySelector('.rp-bar i') as HTMLElement, kda: el.querySelector('.rp-kda') as HTMLElement, resp: el.querySelector('.rp-resp') as HTMLElement,
  }));

  // The slider must not be pulled back under a dragging finger by the playhead it is moving.
  let scrubbing = false;
  seek.addEventListener('pointerdown', () => { scrubbing = true; });
  const endScrub = () => { scrubbing = false; };
  window.addEventListener('pointerup', endScrub);
  seek.addEventListener('input', () => act.seekSec(Number(seek.value)));
  seek.addEventListener('change', endScrub);
  play.addEventListener('click', () => act.toggle());
  speedBtns.forEach(b => b.addEventListener('click', () => act.speed(Number(b.dataset.speed) as Speed)));
  visionBtns.forEach(b => b.addEventListener('click', () => act.vision(b.dataset.vision === 'all' ? null : b.dataset.vision as Team)));
  cards.forEach(c => c.el.addEventListener('click', () => act.hero(c.seat)));
  $('rpLink').addEventListener('click', () => act.copyLink());
  $('rpExit').addEventListener('click', () => act.exit());
  $('rpEndExit').addEventListener('click', () => act.exit());
  $('rpAgain').addEventListener('click', () => act.restart());

  // Space / ←→ drive playback wherever focus is, including on these buttons and the slider (whose own
  // arrow-key step would otherwise fight the ±5 s jump).
  const onKey = (e: KeyboardEvent) => {
    if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
    if (e.code === 'Space') { e.preventDefault(); act.toggle(); }
    else if (e.code === 'ArrowLeft') { e.preventDefault(); act.seekBy(-SEEK_STEP_SEC); }
    else if (e.code === 'ArrowRight') { e.preventDefault(); act.seekBy(SEEK_STEP_SEC); }
  };
  window.addEventListener('keydown', onKey);

  const w = createDomWriter();
  let noteUntil = 0;
  function update(s: ReplayUiState) {
    w.text(play, s.playing ? t('replay.pause') : t('replay.play'));
    w.toggle(play, 'paused', !s.playing);
    w.text(time, `${mmss(s.timeSec)} / ${mmss(s.totalSec)}`);
    if (seek.max !== String(s.totalSec)) seek.max = String(s.totalSec);
    if (!scrubbing) { const v = s.timeSec.toFixed(1); if (seek.value !== v) seek.value = v; }
    for (const b of speedBtns) { const on = Number(b.dataset.speed) === s.speed; w.toggle(b, 'on', on); if (b.getAttribute('aria-pressed') !== String(on)) b.setAttribute('aria-pressed', String(on)); }
    for (const b of visionBtns) { const on = b.dataset.vision === visionKey(s.vision); w.toggle(b, 'on', on); if (b.getAttribute('aria-pressed') !== String(on)) b.setAttribute('aria-pressed', String(on)); }
    for (const c of cards) {
      const h = s.heroes[c.seat];
      if (!h) continue;
      const following = s.follow === c.seat;
      w.toggle(c.el, 'follow', following);
      if (c.el.getAttribute('aria-pressed') !== String(following)) c.el.setAttribute('aria-pressed', String(following));
      w.toggle(c.el, 'dead', !h.alive);
      w.toggle(c.el, 'fogged', !h.visible);
      w.style(c.bar, 'width', `${h.alive ? Math.max(0, Math.min(1, h.hp / h.maxHp)) * 100 : 0}%`);
      w.text(c.kda, `${h.kda[0]} / ${h.kda[1]} / ${h.kda[2]}`);
      w.text(c.resp, !h.alive && h.respawnIn !== null ? t('replay.respawn', { s: Math.max(0, Math.ceil(h.respawnIn)) }) : '');
    }
    w.hidden(end, !s.end);
    if (s.end) { w.text(endTitle, s.end.title); w.text(endSub, s.end.sub); }
    if (noteUntil && performance.now() > noteUntil) { noteUntil = 0; w.hidden(note, true); }
  }

  function flash(text: string) {
    w.text(note, text);
    w.hidden(note, false);
    noteUntil = performance.now() + 2000;
  }

  function destroy() {
    window.removeEventListener('keydown', onKey);
    window.removeEventListener('pointerup', endScrub);
    root.remove();
  }

  return { el: root, update, flash, destroy };
}
